import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Icon, type IconName } from './icons';
import { EmptyState, IconButton, PanelCard } from './primitives';

export function CollectionToolbar({
  primary,
  secondary
}: {
  primary: ReactNode;
  secondary?: ReactNode;
}): JSX.Element {
  return (
    <div className="av-toolbar av-collection-toolbar">
      <div className="av-collection-toolbar__primary">{primary}</div>
      {secondary ? <div className="av-collection-toolbar__secondary">{secondary}</div> : null}
    </div>
  );
}

export function CollectionViewToggle<T extends string>({
  options,
  value,
  onChange
}: {
  options: Array<{ value: T; label: string; icon: IconName; disabled?: boolean }>;
  value: T;
  onChange: (value: T) => void;
}): JSX.Element {
  return (
    <div className="av-view-toggle">
      {options.map((option) => (
        <IconButton
          key={option.value}
          active={option.value === value}
          disabled={option.disabled}
          icon={option.icon}
          label={option.label}
          onClick={() => onChange(option.value)}
        />
      ))}
    </div>
  );
}

export function DataTableSurface({ children }: { children: ReactNode }): JSX.Element {
  return <PanelCard className="av-collection-panel">{children}</PanelCard>;
}

export function CardGridSurface({
  children,
  mode
}: {
  children: ReactNode;
  mode: 'grid' | 'list';
}): JSX.Element {
  return <div className={mode === 'grid' ? 'av-project-grid' : 'av-project-list'}>{children}</div>;
}

export function EmptyCollectionState({ title, description }: { title: string; description: string }): JSX.Element {
  return <EmptyState title={title} description={description} />;
}

export function CollectionSelectionToggle({
  selected,
  label,
  icon,
  imageSrc,
  variant = 'icon',
  className,
  ...props
}: {
  selected: boolean;
  label: string;
  icon?: IconName;
  imageSrc?: string | null;
  variant?: 'icon' | 'check';
  className?: string;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>): JSX.Element {
  const resolvedIcon = variant === 'check' ? 'check' : icon ?? 'check';
  const showOverlayMark = variant === 'icon' && selected;

  return (
    <button
      {...props}
      aria-label={label}
      aria-pressed={selected}
      className={[
        'av-selection-toggle',
        variant === 'check' ? 'av-selection-toggle--check' : '',
        selected ? 'is-selected' : '',
        className ?? ''
      ]
        .filter(Boolean)
        .join(' ')}
      type={props.type ?? 'button'}
    >
      <span className="av-selection-toggle__surface">
        {variant === 'icon' && imageSrc ? <img alt="" className="av-selection-toggle__image" src={imageSrc} /> : <Icon name={resolvedIcon} />}
      </span>
      {showOverlayMark ? (
        <span className="av-selection-toggle__mark" aria-hidden>
          <Icon name="check" />
        </span>
      ) : null}
    </button>
  );
}

export function CollectionSelectionItem({
  icon,
  label,
  subtitle,
  onRemove
}: {
  icon: IconName;
  label: string;
  subtitle?: string | null | undefined;
  onRemove: () => void;
}): JSX.Element {
  return (
    <div className="av-selection-item">
      <div className="av-selection-item__icon">
        <Icon name={icon} />
      </div>
      <div className="av-selection-item__copy">
        <strong>{label}</strong>
        {subtitle ? <span>{subtitle}</span> : null}
      </div>
      <button aria-label={label} className="av-selection-item__remove" type="button" onClick={onRemove}>
        <Icon name="times" />
      </button>
    </div>
  );
}
